/**
 * Máy chủ giả của danh mục model AI cấp nền tảng (ADR-0083, tenant-service): `/api/v1/ai-models` và
 * `/api/v1/tenants/{id}/ai-models`.
 *
 * Từ chối **như backend** để nhánh lỗi của màn chạy được: lỗi ràng buộc kèm `errors[].pointer`.
 * Màu và nhãn không ở đây (`modules/ai-model/constants.ts`): tầng này bị xoá khi có API thật.
 *
 * `mock/ocr.ts` đọc **danh mục hiệu lực** của tenant đang đăng nhập (`modelDungDuoc`) cho ô chọn model
 * ở cổng tenant, như module ocr đọc `/internal/v1/tenants/{id}/ai-models` (ADR-0083 mục 4) — tắt một
 * model cho tenant ở cổng nền tảng là ô chọn ở cổng tenant đó mất model ngay.
 *
 * Tình huống mẫu để soát:
 * - tenant Minh Long (`…0002`, tenant của tài khoản mẫu cổng tenant) đã bị tắt `claude`, `gemini-2.5-pro`;
 * - tắt model đang mặc định → `400` pointer `#/is_active`;
 * - tên toàn ký hiệu, không có model id → `400` pointer `#/label` (không sinh được khoá);
 * - model id trùng khoá đã có (`claude`) → khoá thêm hậu tố `-2`.
 */
import type { ApiError, LoiTruong } from '../lib/errors';
import { AI_ENGINES } from '../modules/ai-model/types';
import type {
  AiEngine,
  AiModel,
  AiModelCreate,
  AiModelMultiplierSuggestion,
  AiModelMultiplierSuggestionList,
  AiModelUpdate,
  TenantAiModel,
} from '../modules/ai-model/types';
import { sinhKhoaModel } from '../modules/ai-model/nghiep-vu/ai-model';
import { heSoHoaVon } from '../modules/goi-nap-token/nghiep-vu/fox';

let dem = 2000;
const bayGio = () => new Date().toISOString();
const sao = <T>(x: T): T => structuredClone(x);

function loi(status: number, code: string, message: string, details?: LoiTruong[]): ApiError {
  return { status, code, message, ...(details ? { details } : {}) };
}
const saiThongTin = (errors: LoiTruong[]) =>
  loi(400, 'INVALID_ARGUMENT', 'Thông tin model AI chưa đúng, vui lòng sửa các trường được đánh dấu.', errors);

const mau = (
  key: string,
  label: string,
  engine: AiEngine,
  model_id: string | null,
  thinking_mode: AiModel['thinking_mode'],
  display_order: number,
  phu: Partial<AiModel> = {},
): AiModel => ({
  id: `am-${key}`,
  key,
  label,
  engine,
  model_id,
  vision_model_id: null,
  thinking_mode,
  description: null,
  is_active: true,
  is_default: false,
  display_order,
  he_so_dau_vao: 1,
  he_so_cache: null,
  he_so_dau_ra: 1,
  ten_model_gia: null,
  created_at: '2026-10-23T02:00:00Z',
  updated_at: '2026-10-23T02:00:00Z',
  ...phu,
});

/** 14 model mẫu của `V20` (chép từ `V1.1.0` module ocr, cùng `key`) cộng một model nền tảng thêm sau. */
const MODELS: AiModel[] = [
  mau('gemini', 'Gemini 2.5 Flash', 'GEMINI', 'gemini-2.5-flash', 'OPTIONAL', 10, { he_so_dau_vao: 0.15, he_so_cache: 0.1, he_so_dau_ra: 1.2 }),
  mau('gemini-2.5-pro', 'Gemini 2.5 Pro', 'GEMINI', 'gemini-3.1-pro-preview', 'FORCED', 20, { he_so_dau_vao: 0.6, he_so_cache: null, he_so_dau_ra: 3.8 }),
  mau('gemini-3.5-flash', 'Gemini 3.5', 'GEMINI', 'gemini-3.5-flash', 'OPTIONAL', 30),
  mau('claude', 'Claude Sonnet 4.5', 'CLAUDE', 'claude-sonnet-4-5', 'OPTIONAL', 40, { he_so_dau_vao: 1.5, he_so_cache: 0.15, he_so_dau_ra: 5.5 }),
  mau('foxai', 'FoxAI', 'FOXAI', null, 'OPTIONAL', 50, { is_default: true, he_so_dau_vao: 0.2, he_so_dau_ra: 0.5 }),
  mau('deepseek-ocr2', 'Deepseek-ocr2', 'DEEPSEEK', 'deepseek-ocr2', 'DISABLED', 60, {
    description: 'Model vision chuyên OCR tự vận hành, đọc thẳng ảnh/PDF scan. PDF được dựng sang PNG trước khi gửi.',
  }),
  mau('chandra', 'Chandra (OCR tự vận hành)', 'CHANDRA', null, 'DISABLED', 70, {
    description: 'Model vision chuyên OCR, đọc thẳng ảnh/PDF scan. PDF được dựng sang PNG trước khi gửi.',
  }),
  mau('chandra2-ocr', 'Chandra 2 (OCR)', 'CHANDRA2', 'chandra2-ocr', 'DISABLED', 80),
  mau('paddleocr-vl', 'PaddleOCR-VL 1.6 (OCR)', 'PADDLE', 'PaddleOCR-VL-1.6', 'DISABLED', 90, { is_active: false }),
  mau('chandra2-ocr-l40', 'Chandra 2 – L40 (OCR)', 'CHANDRA2_L40', 'chandra2-ocr', 'DISABLED', 100),
  mau('qwen3.8-27b', 'Qwen 3.8 27B', 'QWEN38', 'qwen3.8-27b', 'OPTIONAL', 110, {
    description: 'Model đa phương thức tự vận hành trên cụm L40: đọc được ảnh, điền dữ liệu vào cấu hình và gợi ý trường/bảng.',
  }),
  mau('chandra2-ocr-h200', 'Chandra 2 – H200 (OCR)', 'CHANDRA2_H200', 'chandra2-ocr', 'DISABLED', 120, { is_active: false }),
  mau('qwen3.8-27b-h200', 'Qwen 3.8 27B – H200', 'QWEN38_H200', 'qwen3.8-27b', 'OPTIONAL', 130),
  mau('gemma4-ocr', 'Gemma 4 26B-A4B', 'GEMMA4', 'gemma4-ocr', 'OPTIONAL', 140),
  mau('gemini-2-5-flash', 'Gemini cho bảng kê nhiều trang của chi nhánh phía Nam (bản thử)', 'GEMINI', 'gemini-2.5-flash', 'OPTIONAL', 150, {
    vision_model_id: 'gemini-2.5-flash-lite',
    description: 'Tách trang PDF trước khi gửi, dùng cho bảng kê dài.',
    created_at: '2026-10-23T03:15:00Z',
    updated_at: '2026-10-23T03:15:00Z',
  }),
];

/** `tenant_ai_model_off`: chỉ ghi model bị tắt cho từng tenant. */
const TAT_THEO_TENANT: Record<string, Set<string>> = {
  '0a7d3c5e-1f2b-4e6a-9c8d-000000000002': new Set(['claude', 'gemini-2.5-pro']),
};

const theoThuTu = (a: AiModel, b: AiModel) => a.display_order - b.display_order || a.label.localeCompare(b.label);

function modelTheoId(id: string): AiModel {
  const m = MODELS.find((x) => x.id === id);
  if (!m) throw loi(404, 'NOT_FOUND', 'Không tìm thấy model AI.');
  return m;
}

function kiemTruong(du: AiModelCreate | AiModelUpdate, taoMoi: boolean): LoiTruong[] {
  const errors: LoiTruong[] = [];
  if (taoMoi || du.label !== undefined) {
    const ten = du.label?.trim() ?? '';
    if (!ten) errors.push({ pointer: '#/label', detail: 'Không được để trống.' });
    else if (ten.length > 120) errors.push({ pointer: '#/label', detail: 'Tối đa 120 ký tự.' });
  }
  for (const k of ['model_id', 'vision_model_id'] as const) {
    if ((du[k]?.trim().length ?? 0) > 120) errors.push({ pointer: `#/${k}`, detail: 'Tối đa 120 ký tự.' });
  }
  if ((du.description?.trim().length ?? 0) > 500) errors.push({ pointer: '#/description', detail: 'Tối đa 500 ký tự.' });
  for (const k of ['he_so_dau_vao', 'he_so_cache', 'he_so_dau_ra'] as const) {
    const v = du[k];
    if (v !== undefined && v !== null && !(v >= 0.001 && v <= 1000)) {
      errors.push({ pointer: `#/${k}`, detail: 'Hệ số token phải từ 0,001 tới 1000, tối đa 3 chữ số thập phân, ví dụ 1,5.' });
    }
  }
  return errors;
}

/**
 * Danh mục hiệu lực của một tenant (`GET /internal/v1/tenants/{id}/ai-models`): model đang bật, không
 * bị tắt cho tenant; `is_default` tính lại — mặc định nền tảng nếu dùng được, không thì model đầu tiên.
 */
export function modelDungDuoc(tenantId: string | undefined): AiModel[] {
  const tat = (tenantId && TAT_THEO_TENANT[tenantId]) || new Set<string>();
  const ds = MODELS.filter((m) => m.is_active && !tat.has(m.key)).sort(theoThuTu).map(sao);
  const macDinh = ds.find((m) => m.is_default) ?? ds[0];
  return ds.map((m) => ({ ...m, is_default: m === macDinh }));
}

/**
 * Giá vốn (USD cho 1 triệu token thật: đầu vào, đọc cache, đầu ra) theo tên model giá, như Bảng giá model trả cho
 * tenant-service. Cache `null` là chiều đó thiếu giá vốn: `status` vẫn `OK`, hoà vốn của riêng chiều đó `null`.
 */
const GIA_VON_MAU: Record<string, [string, string | null, string]> = {
  'gemini-2.5-flash': ['0.3', '0.075', '2.5'],
  'gemini-3.1-pro-preview': ['1.25', '0.31', '10'],
  'gemini-3.5-flash': ['0.5', '0.125', '3'],
  'claude-sonnet-4-5': ['3', '0.3', '15'],
  'qwen3.8-27b': ['0.2', null, '0.6'],
};

export const mayChuModelAiNenTang = {
  danhSach(): { items: AiModel[] } {
    return { items: sao([...MODELS].sort(theoThuTu)) };
  },

  /**
   * `GET /api/v1/ai-models/multiplier-suggestions` (ADR-0123 mục 7), đơn giá lấy từ giá bán token Fox. Mẫu: Gemini 2.5
   * Flash có lãi cả ba chiều; Gemini 2.5 Pro để cache trống và đầu ra dưới hoà vốn; Claude dưới hoà vốn ở đầu ra.
   */
  goiYHeSo(donGia: string | null): AiModelMultiplierSuggestionList {
    return {
      don_gia_usd_moi_trieu_token_fox: donGia,
      items: [...MODELS].sort(theoThuTu).map((m) => {
        const ten = m.ten_model_gia ?? m.model_id;
        const gia = ten ? GIA_VON_MAU[ten] : undefined;
        const giaCache = gia?.[1] ?? null;
        const status: AiModelMultiplierSuggestion['status'] = !ten
          ? 'NO_PRICE_MODEL_NAME'
          : !gia
            ? 'NO_PRICE'
            : donGia === null
              ? 'NO_UNIT_PRICE'
              : 'OK';
        return {
          model_id: m.id,
          key: m.key,
          label: m.label,
          is_active: m.is_active,
          price_model_name: ten,
          he_so_dau_vao: m.he_so_dau_vao,
          he_so_cache: m.he_so_cache,
          he_so_dau_ra: m.he_so_dau_ra,
          hoa_von_dau_vao: status === 'OK' ? heSoHoaVon(gia?.[0] ?? null, donGia) : null,
          hoa_von_cache: status === 'OK' ? heSoHoaVon(giaCache, donGia) : null,
          hoa_von_dau_ra: status === 'OK' ? heSoHoaVon(gia?.[2] ?? null, donGia) : null,
          status,
          input_usd_per_million: gia?.[0] ?? null,
          cache_read_usd_per_million: giaCache,
          output_usd_per_million: gia?.[2] ?? null,
          price_id: gia ? 1 : null,
          model_pattern: gia ? ten : null,
          currency: gia ? 'USD' : null,
        };
      }),
    };
  },

  tao(du: AiModelCreate): AiModel {
    const errors = kiemTruong(du, true);
    if (!AI_ENGINES.includes(du.engine)) errors.push({ pointer: '#/engine', detail: 'Engine không có trong module ocr.' });
    const key = sinhKhoaModel(du.model_id ?? '', du.label ?? '', MODELS.map((m) => m.key));
    if (!errors.length && !key) {
      errors.push({ pointer: '#/label', detail: 'Không sinh được khoá từ model id hoặc tên.' });
    }
    if (du.is_default && du.is_active === false) {
      errors.push({ pointer: '#/is_default', detail: 'Model mặc định phải đang bật.' });
    }
    if (errors.length) throw saiThongTin(errors);
    if (du.is_default) MODELS.forEach((m) => (m.is_default = false));
    const m: AiModel = {
      id: `am-${(dem += 1).toString(16)}-mock`,
      key,
      label: du.label.trim(),
      engine: du.engine,
      model_id: du.model_id?.trim() || null,
      vision_model_id: du.vision_model_id?.trim() || null,
      thinking_mode: du.thinking_mode ?? 'OPTIONAL',
      description: du.description?.trim() || null,
      is_active: du.is_active ?? true,
      is_default: du.is_default ?? false,
      display_order: du.display_order ?? Math.max(0, ...MODELS.map((x) => x.display_order)) + 10,
      he_so_dau_vao: du.he_so_dau_vao ?? 1,
      he_so_cache: du.he_so_cache ?? null,
      he_so_dau_ra: du.he_so_dau_ra ?? 1,
      ten_model_gia: du.ten_model_gia?.trim() || null,
      created_at: bayGio(),
      updated_at: bayGio(),
    };
    MODELS.push(m);
    return sao(m);
  },

  sua(id: string, du: AiModelUpdate): AiModel {
    const m = modelTheoId(id);
    const errors = kiemTruong(du, false);
    if (du.is_active === false && m.is_default) {
      errors.push({ pointer: '#/is_active', detail: 'Model đang là mặc định: đặt model khác làm mặc định trước khi tắt.' });
    }
    if (du.is_default === true && !(du.is_active ?? m.is_active)) {
      errors.push({ pointer: '#/is_default', detail: 'Model mặc định phải đang bật.' });
    }
    if (du.is_default === false && m.is_default) {
      errors.push({ pointer: '#/is_default', detail: 'Phải luôn có một model mặc định: đặt model khác làm mặc định.' });
    }
    if (errors.length) throw saiThongTin(errors);
    if (du.is_default) MODELS.forEach((x) => (x.is_default = false));
    const rong = (v: string | null | undefined) => v?.trim() || null;
    Object.assign(m, {
      ...du,
      ...(du.label !== undefined ? { label: du.label.trim() } : {}),
      ...(du.model_id !== undefined ? { model_id: rong(du.model_id) } : {}),
      ...(du.vision_model_id !== undefined ? { vision_model_id: rong(du.vision_model_id) } : {}),
      ...(du.description !== undefined ? { description: rong(du.description) } : {}),
      ...(du.ten_model_gia !== undefined ? { ten_model_gia: rong(du.ten_model_gia) } : {}),
      updated_at: bayGio(),
    });
    return sao(m);
  },

  theoTenant(tenantId: string): { items: TenantAiModel[] } {
    const tat = TAT_THEO_TENANT[tenantId] ?? new Set<string>();
    return {
      items: MODELS.filter((m) => m.is_active)
        .sort(theoThuTu)
        .map((m) => ({ model: sao(m), enabled: !tat.has(m.key) })),
    };
  },

  datTat(tenantId: string, disabledKeys: string[]): { items: TenantAiModel[] } {
    const errors: LoiTruong[] = disabledKeys.flatMap((k, i) =>
      MODELS.some((m) => m.key === k) ? [] : [{ pointer: `#/disabled_keys/${i}`, detail: `Không có model "${k}" trong danh mục.` }],
    );
    if (errors.length) throw saiThongTin(errors);
    TAT_THEO_TENANT[tenantId] = new Set(disabledKeys);
    return this.theoTenant(tenantId);
  },
};
